// components/PendingApprovalsPage.tsx
import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Col, Row } from 'react-bootstrap';
import { faFilter, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SearchBox from 'components/common/SearchBox';
import CompanyFilter from 'components/common/CompanyFilter';
import { fetchPendingApprovals } from 'store/approvalsSlice';
import { fetchCompanies } from 'store/companySlice';
import { AppDispatch, RootState } from 'store';
import PendingApprovalsTable from 'components/devices/PendingApprovalsTable';
import PhoenixLoader from 'components/common/PhoenixLoader';

const PendingApprovalsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const requests = useSelector((state: RootState) => state.approvals.pending);
  const loading = useSelector((state: RootState) => state.approvals.loading);
  const error = useSelector((state: RootState) => state.approvals.error);

  // Get companies data and loading state
  const companies = useSelector((state: RootState) => state.companies.data);
  const companiesLoading = useSelector(
    (state: RootState) => state.companies.loading
  );

  // Get auth state
  const authState = useSelector((state: RootState) => state.auth);
  const { companyId: authCompanyId, role } = authState;

  // Check if user is super admin
  const isSuperAdmin =
    !authCompanyId ||
    (role && typeof role === 'string' && role.toLowerCase().includes('super'));

  // Determine company ID for fetching approvals
  const companyIdForFetch = isSuperAdmin
    ? 0
    : companies.length > 0
    ? companies[0].id
    : null;

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(
    null
  );
  const [showFilterPanel, setShowFilterPanel] = useState(false);

  // Fetch companies on component mount
  useEffect(() => {
    if (companies.length === 0) {
      dispatch(fetchCompanies());
    }
  }, [dispatch, companies.length]);

  // Handle company selection
  const handleCompanySelect = (companyId: number | null) => {
    setSelectedCompanyId(companyId);
  };

  // Fetch pending approvals
  useEffect(() => {
    if (isSuperAdmin) {
      dispatch(fetchPendingApprovals(0));
    } else if (companyIdForFetch) {
      dispatch(fetchPendingApprovals(companyIdForFetch));
    }
  }, [dispatch, companyIdForFetch, isSuperAdmin, companies.length]);

  // Filter requests based on company selection
  const filteredRequests = useMemo(() => {
    let result = requests;

    if (isSuperAdmin && selectedCompanyId) {
      result = result.filter(
        (request: any) => request.companyId === selectedCompanyId
      );
    }

    return result;
  }, [requests, isSuperAdmin, selectedCompanyId]);

  // Show loading while fetching companies (only for non-super admins)
  if (companiesLoading && !isSuperAdmin) {
    return (
      <div className="container py-4">
        <div className="text-center py-4">
          <PhoenixLoader />
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h2
          className="mb-0"
          style={{
            fontSize: '1.5rem',
            fontWeight: '600',
            color: '#374151',
            letterSpacing: '-0.025em'
          }}
        >
          <FontAwesomeIcon icon={faCheckCircle} className="me-2 text-primary" />
          Pending Approvals
        </h2>
        <Row className="g-3 justify-content-end mb-0">
          <Col xs="auto">
            <div className="d-flex gap-2">
              <button
                className="btn d-flex align-items-center gap-2"
                style={{
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '0.375rem',
                  padding: '0.375rem 0.75rem',
                  fontSize: '0.875rem',
                  color: '#374151',
                  fontWeight: '400'
                }}
                onClick={() => setShowFilterPanel(!showFilterPanel)}
              >
                <FontAwesomeIcon
                  icon={faFilter}
                  style={{ fontSize: '0.875rem' }}
                />
                <span>Filter</span>
              </button>
              <SearchBox
                placeholder="Search by department, request ID, or status..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </div>
          </Col>
        </Row>

        {/* Filter Panel */}
        {showFilterPanel && isSuperAdmin && companies.length > 0 && (
          <div className="mb-3 mt-3">
            <div className="d-flex gap-2">
              <CompanyFilter
                companies={companies}
                selectedCompanyId={selectedCompanyId}
                onCompanySelect={handleCompanySelect}
              />
            </div>
          </div>
        )}

        <div className="mt-3">
          {/* Loading State */}
          {loading && (
            <div className="d-flex flex-column justify-content-center align-items-center min-vh-50">
              <PhoenixLoader />
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="alert alert-danger" role="alert">
              Error: {error}
            </div>
          )}

          {/* No Company Found (only for non-super admins) */}
          {!isSuperAdmin &&
            !companyIdForFetch &&
            companies.length === 0 &&
            !companiesLoading && (
              <div className="alert alert-warning text-center">
                No companies found. Please check your company setup.
              </div>
            )}

          {/* Empty State */}
          {!loading && !error && filteredRequests.length === 0 && (
            <div className="text-center py-4">
              <h5>No Pending Approval Requests Found</h5>
              <p className="text-muted">
                {isSuperAdmin && selectedCompanyId
                  ? 'There are no pending approval requests for the selected company.'
                  : isSuperAdmin
                  ? 'There are no pending approval requests across all companies.'
                  : 'There are no pending approval requests in your company.'}
              </p>
            </div>
          )}

          {/* Table */}
          {!loading && !error && filteredRequests.length > 0 && (
            <PendingApprovalsTable
              requests={filteredRequests}
              searchQuery={searchQuery}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default PendingApprovalsPage;
